import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { z } from "zod";
import { LlmProviderSchema, getOrgLlmCredentialsResponseSchema, llmCredentialStatusSchema, setOrgLlmCredentialBodySchema, testOrgLlmCredentialBodySchema, testOrgLlmCredentialResponseSchema } from "@zion/sdk-contracts";
//#region src/llm-credentials.ts
/**
* `/v1/admin/llm-credentials` — the org's per-provider LLM API keys.
*
* Producer `OrgLlmCredentialsController` in core-api, which lives under `src/llm/`, not
* `src/admin/`. Every shape already lives in `@zion/sdk-contracts` (`llm-credentials.ts`), so
* this file declares routes, two path builders and the request and response schemas over those
* shapes.
*
* Permission `org:llm-credentials:manage` on the class, so the read is gated on the write key.
* No response here carries plaintext: a status is `{ configured, preview, updatedAt }`, asserted
* on the way out by `assertNoForbiddenFields` and re-stripped by the global masking interceptor.
*
* core-api keeps each key in its tier-1 managed-secret store (`ManagedSecretsService`, keyed
* `appId '__llm__'`). The core-api comments mark this surface deprecated in favour of the general
* managed-secret app routes. Plan 2026-10-14-app-pools-runtime-deletion (ruling R-067) deleted
* those routes, so these four routes are the only admin API over the org LLM keys. core-api also
* has two internal routes over the same keys: `POST internal/v1/llm/seed` (`LlmSeedController`)
* writes them, and `POST internal/v1/llm/resolve-key` (`LlmInternalController`) returns a key in
* plaintext. These four routes send no
* `Deprecation` or `Sunset` header, so the compatibility window in this package's README has not
* started for them.
*
* The `:provider` segment is `openai` or `anthropic` and nothing else. The controller parses it
* with `parseProviderParam`, which answers 400 for any other value, so
* {@link adminLlmCredentialPath} refuses the same set before a request is built.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client.
* No shape in this file travels in both directions.
* Every shape here comes from `@zion/sdk-contracts`, and that package stays their owner. The
* three responses read views rebuilt with `z.object(upstream.shape)` (R-38), and the credential
* status is relaxed at both levels: the per-provider status and the two-provider object that
* carries it. Tests pin each view's key set against its upstream.
*/
/** The collection path. No parameter, so the OpenAPI form and the request form are the same. */
const ADMIN_LLM_CREDENTIALS_PATH = `${ADMIN_API_PREFIX}/llm-credentials`;
/** One provider, in OpenAPI `{param}` form. Fill it with {@link adminLlmCredentialPath}. */
const ADMIN_LLM_CREDENTIAL_PATH_TEMPLATE = `${ADMIN_LLM_CREDENTIALS_PATH}/{provider}`;
/** The test action, in OpenAPI `{param}` form. Fill it with {@link adminLlmCredentialTestPath}. */
const ADMIN_LLM_CREDENTIAL_TEST_PATH_TEMPLATE = `${ADMIN_LLM_CREDENTIAL_PATH_TEMPLATE}/test`;
/**
* `GET /v1/admin/llm-credentials`. Both providers' status in one object, always both keys.
* Permission `org:llm-credentials:manage`.
*/
const LIST_LLM_CREDENTIALS_ROUTE = route("GET", ADMIN_LLM_CREDENTIALS_PATH);
/**
* `PUT /v1/admin/llm-credentials/{provider}`. Sets or replaces one provider's key; 200 with that
* provider's status. Permission `org:llm-credentials:manage`.
*/
const PUT_LLM_CREDENTIAL_ROUTE = route("PUT", ADMIN_LLM_CREDENTIAL_PATH_TEMPLATE);
/**
* `DELETE /v1/admin/llm-credentials/{provider}`. Removes one provider's key; 204 and no body.
* Permission `org:llm-credentials:manage`.
*/
const DELETE_LLM_CREDENTIAL_ROUTE = route("DELETE", ADMIN_LLM_CREDENTIAL_PATH_TEMPLATE);
/**
* `POST /v1/admin/llm-credentials/{provider}/test`. Tests the supplied key, or the stored key
* when the body omits `apiKey`; 200 with `{ ok, detail }`. Permission
* `org:llm-credentials:manage`.
*/
const TEST_LLM_CREDENTIAL_ROUTE = route("POST", ADMIN_LLM_CREDENTIAL_TEST_PATH_TEMPLATE);
/**
* Fill {@link ADMIN_LLM_CREDENTIAL_PATH_TEMPLATE} with one provider.
*
* @param provider `'openai'` or `'anthropic'`. Parsed with `LlmProviderSchema` before the path is
*   built, so a value the controller would answer 400 for throws here instead.
* @returns the request path, each segment percent-encoded.
*/
function adminLlmCredentialPath(provider) {
	return fillPath(ADMIN_LLM_CREDENTIAL_PATH_TEMPLATE, { provider: LlmProviderSchema.parse(provider) });
}
/**
* Fill {@link ADMIN_LLM_CREDENTIAL_TEST_PATH_TEMPLATE} with one provider.
*
* @param provider `'openai'` or `'anthropic'`. Parsed with `LlmProviderSchema`, as
*   {@link adminLlmCredentialPath} does.
* @returns the request path, each segment percent-encoded.
*/
function adminLlmCredentialTestPath(provider) {
	return fillPath(ADMIN_LLM_CREDENTIAL_TEST_PATH_TEMPLATE, { provider: LlmProviderSchema.parse(provider) });
}
/**
* One provider's credential status, relaxed to strip an unknown member (R-38).
*
* `llmCredentialStatusSchema` in `@zion/sdk-contracts` owns this shape and stays its home. This is
* `z.object(upstream.shape)`: `{ configured, preview, updatedAt }`, the strictness dropped, and no
* `.refine()` upstream to lose.
*
* ⛔ Stripping does not weaken the no-plaintext rule. `apiKey` is not a member, so a response
* carrying one still fails to satisfy the shape's own members and the key never reaches a typed
* reader; the server's `assertNoForbiddenFields` and the global masking interceptor are the
* controls that stop it being sent at all.
*/
const llmCredentialStatusViewSchema = z.object(llmCredentialStatusSchema.shape);
/**
* `GET /v1/admin/llm-credentials` response: one status per provider, both keys always present, an
* unset provider reading `{ configured: false, preview: null, updatedAt: null }`.
*
* `getOrgLlmCredentialsResponseSchema` in `@zion/sdk-contracts` owns the shape. Rebuilt from its
* `.shape` and then re-pointed at {@link llmCredentialStatusViewSchema}, because relaxing only the
* outer object would leave each nested status strict and a member added inside one would still red
* the caller. The key set is untouched, so the drift test still holds.
*/
const listLlmCredentialsResponseSchema = z.object(getOrgLlmCredentialsResponseSchema.shape).extend({
	openai: llmCredentialStatusViewSchema,
	anthropic: llmCredentialStatusViewSchema
});
/**
* `PUT /v1/admin/llm-credentials/{provider}` body. Owned by `@zion/sdk-contracts`
* (`setOrgLlmCredentialBodySchema`): `apiKey` is required, 8 to 512 characters, charset
* unrestricted because provider key formats differ.
*/
const putLlmCredentialRequestSchema = setOrgLlmCredentialBodySchema;
/**
* `PUT /v1/admin/llm-credentials/{provider}` response: that provider's status. Never plaintext —
* see {@link llmCredentialStatusViewSchema}.
*/
const putLlmCredentialResponseSchema = llmCredentialStatusViewSchema;
/**
* `POST /v1/admin/llm-credentials/{provider}/test` body. Owned by `@zion/sdk-contracts`
* (`testOrgLlmCredentialBodySchema`): `apiKey` is optional, and omitting it tests the stored key.
* The shell sends `{}` in that case, which this schema accepts.
*/
const testLlmCredentialRequestSchema = testOrgLlmCredentialBodySchema;
/**
* `POST /v1/admin/llm-credentials/{provider}/test` response: `{ ok, detail }`, `detail` null on
* success.
*
* `testOrgLlmCredentialResponseSchema` in `@zion/sdk-contracts` owns the shape; this is
* `z.object(upstream.shape)` so an added member is stripped (R-38).
*/
const testLlmCredentialResponseSchema = z.object(testOrgLlmCredentialResponseSchema.shape);
/** Every route this file declares, for the package's route census. */
const llmCredentialsRoutes = Object.freeze([
	LIST_LLM_CREDENTIALS_ROUTE,
	PUT_LLM_CREDENTIAL_ROUTE,
	DELETE_LLM_CREDENTIAL_ROUTE,
	TEST_LLM_CREDENTIAL_ROUTE
]);
//#endregion
export { ADMIN_LLM_CREDENTIALS_PATH, ADMIN_LLM_CREDENTIAL_PATH_TEMPLATE, ADMIN_LLM_CREDENTIAL_TEST_PATH_TEMPLATE, DELETE_LLM_CREDENTIAL_ROUTE, LIST_LLM_CREDENTIALS_ROUTE, PUT_LLM_CREDENTIAL_ROUTE, TEST_LLM_CREDENTIAL_ROUTE, adminLlmCredentialPath, adminLlmCredentialTestPath, listLlmCredentialsResponseSchema, llmCredentialStatusViewSchema, llmCredentialsRoutes, putLlmCredentialRequestSchema, putLlmCredentialResponseSchema, testLlmCredentialRequestSchema, testLlmCredentialResponseSchema };

//# sourceMappingURL=llm-credentials.js.map